// Stripe quality (Feature P5). A pure score over the final cut grid, the copy
// tiers for the results line, and the tip bonus it pays. Nothing here changes
// `tipPct`: the bonus is added AFTER it, on its own line, and never deducts.
//
// What gets scored: grass cells the mower cut (cut = 1, not a trim-only ring
// cell). `grid.stripe` holds each cell's last pass heading, quantised 0..255.
//
// The score (0–100) is three parts, every cell weighted 1, front of house 2:
//
//   A  row agreement (65%): each cell's heading vs its two neighbours along the
//      pass axis — the dominant axis for row cells, the cell's own heading for
//      a headland lap. Same axis within 25° agrees; an exact reverse agrees too
//      (alternating passes are the goal).
//   S  row spacing (35%): scan across the dominant axis, split into bands where
//      the pass direction flips, and check each interior band's width against
//      multiples of the deck width (or of the median band, so a steady overlap
//      reads as even rows). Edge bands clipped by the lawn aren't judged.
//   F  fragments: cells in a patch of same-heading cells smaller than
//      max(6, 3 × deck²) cells, unless the patch is a short run: at least
//      max(4, 2 × deck) cells long along its own heading (a stripe in a small
//      corner of the lawn, a side of a headland lap).
//      score = (0.65 A + 0.35 S) × (1 − 0.6 F).
//
// Curved islands: a cell near a round obstacle (a prop with `r`, or a compact
// island of bed/obstacle cells) whose heading runs tangential to it is a neat
// circling pass: it agrees, and is never a fragment. So is a headland lap: a
// cell within two decks (and the trim ring) of the lawn's edge whose heading
// runs along the edge.
//
// Front of house (weight 2): the `nature-strip` zone, plus the half of the lot
// (the grass bounding box) nearest the spawn, which is where the ute pulls up
// off the street.

import { CELL_TYPES, ZONE_IDS, type Grid, type JobResult, type Layout } from './types';
import { bonusAud } from './vibe';
import { CROSS_Q, axisGap } from './cells';

const TAU = Math.PI * 2;
const Q = TAU / 256;
const T_GRASS = CELL_TYPES.indexOf('grass');
const T_BED = CELL_TYPES.indexOf('bed');
const T_OBSTACLE = CELL_TYPES.indexOf('obstacle');
const Z_NATURE_STRIP = ZONE_IDS.indexOf('nature-strip');

/** Headings within this (radians, undirected) agree. */
export const STRIPE_AGREE_RAD = (25 * Math.PI) / 180;
/** Fewer scored cells than this and there's nothing to rate (score is null). */
export const STRIPE_MIN_CELLS = 12;
/** A patch this many cells long along its heading is a short stripe, not a fragment. */
const RUN_CELLS = 4;
/** Cell weight in the front-of-house mask. */
export const FRONT_WEIGHT = 2;

export interface StripeIsland {
  /** Centre, metres. */
  x: number;
  y: number;
  /** Radius, metres. */
  r: number;
}

export interface StripeOptions {
  /** The mower's deck width in cells (MowerState.deckCells). */
  deckCells: number;
  /** Round obstacles a circling pass may go around. */
  islands?: readonly StripeIsland[];
  /** 1 = front of house (counts double). Same shape as the grid. */
  front?: Uint8Array | null;
}

function axisDiff(a: number, b: number): number {
  let d = Math.abs(a - b) % Math.PI;
  if (d > Math.PI / 2) d = Math.PI - d;
  return d;
}

function dirDiff(a: number, b: number): number {
  let d = Math.abs(a - b) % TAU;
  if (d > Math.PI) d = TAU - d;
  return d;
}

/** 8-neighbour step along an (undirected) axis angle: [dx, dy]. */
const STEPS: readonly (readonly [number, number])[] = [[1, 0], [1, 1], [0, 1], [-1, 1]];
function stepFor(angle: number): readonly [number, number] {
  const k = ((Math.round(angle / (Math.PI / 4)) % 4) + 4) % 4;
  return STEPS[k];
}

/** Fit of a band width to multiples of `unit`: 1 within 0.2 of a multiple, 0 at 0.5 away. */
function fitTo(width: number, unit: number): number {
  if (!(unit > 0)) return 0;
  const x = width / unit;
  const k = Math.max(1, Math.round(x));
  const err = Math.abs(x - k);
  return err <= 0.2 ? 1 : Math.max(0, 1 - (err - 0.2) / 0.3);
}

/**
 * Interior bands across axis `theta`: scan lines perpendicular to it, split
 * into runs where the pass direction along it flips; a run bounded by
 * opposite passes on both sides is a band. Width in cells (× the scan step's
 * length), weight = the cells' weights.
 */
function bandRuns(
  grid: Grid, scored: Uint8Array, tang: Uint8Array, ang: Float32Array, wt: Float32Array, theta: number,
): { width: number; weight: number }[] {
  const { w, h } = grid;
  const n = w * h;
  const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? y * w + x : -1);
  const [ax, ay] = stepFor(theta);
  // Across a diagonal axis, scan along x: each step crosses one diagonal line of cells (1/√2 cells apart).
  const diag = ax !== 0 && ay !== 0;
  const px = diag ? 1 : -ay, py = diag ? 0 : ax;
  const stepLen = diag ? Math.SQRT1_2 : Math.hypot(px, py);
  const cls = new Int8Array(n); // +1 / −1 pass direction along the axis, 0 = not a row cell
  for (let i = 0; i < n; i++) {
    if (!scored[i] || tang[i] || axisDiff(ang[i], theta) > STRIPE_AGREE_RAD) continue;
    cls[i] = Math.cos(ang[i] - theta) >= 0 ? 1 : -1;
  }
  const bands: { width: number; weight: number }[] = [];
  for (let y0 = 0; y0 < h; y0++) {
    for (let x0 = 0; x0 < w; x0++) {
      if (at(x0 - px, y0 - py) >= 0) continue; // not the start of a scan line
      let x = x0, y = y0;
      let prev = 0, runCls = 0, runLen = 0, runW = 0;
      for (let i = at(x, y); i >= 0; x += px, y += py, i = at(x, y)) {
        const c = cls[i];
        if (c !== runCls) {
          // A run closes: interior when bounded by opposite passes on both sides.
          if (runCls !== 0 && prev === -runCls && c === -runCls) bands.push({ width: runLen * stepLen, weight: runW });
          prev = runCls;
          runCls = c; runLen = 0; runW = 0;
        }
        if (c !== 0) { runLen++; runW += wt[i]; }
      }
    }
  }
  return bands;
}

/**
 * Stripe score 0–100 for the cut grid, or null when too little was mowed to
 * rate. Pure: reads the grid, allocates its own scratch.
 */
export function stripeScore(grid: Grid, opts: StripeOptions): number | null {
  const { w, h, cellM } = grid;
  const n = w * h;
  const deck = Math.max(1, opts.deckCells || 1);
  const scored = new Uint8Array(n);
  const ang = new Float32Array(n);
  const wt = new Float32Array(n);
  let count = 0;
  for (let i = 0; i < n; i++) {
    if (grid.type[i] !== T_GRASS || !grid.cut[i] || grid.trim[i]) continue;
    scored[i] = 1;
    ang[i] = grid.stripe[i] * Q;
    wt[i] = opts.front?.[i] ? FRONT_WEIGHT : 1;
    count++;
  }
  if (count < STRIPE_MIN_CELLS) return null;

  // Tangential to a round obstacle: a neat circling pass.
  const tang = new Uint8Array(n);
  const islands = opts.islands ?? [];
  if (islands.length) {
    const reach = Math.max(2, 3 * deck * cellM);
    for (let i = 0; i < n; i++) {
      if (!scored[i]) continue;
      const px = ((i % w) + 0.5) * cellM, py = (((i / w) | 0) + 0.5) * cellM;
      for (const isl of islands) {
        const dx = px - isl.x, dy = py - isl.y;
        const d = Math.hypot(dx, dy);
        if (d < 1e-6 || d > isl.r + reach) continue;
        if (axisDiff(ang[i], Math.atan2(dy, dx) + Math.PI / 2) <= STRIPE_AGREE_RAD) { tang[i] = 1; break; }
      }
    }
  }

  // A headland lap: a cell in the lawn's outer band (two decks and the trim
  // ring) running along the edge is as neat as a stripe, whatever it meets.
  const edge = edgeChamfer(grid);
  const band = (2 * deck + 1) * 3;
  for (let i = 0; i < n; i++) {
    if (!scored[i] || tang[i] || edge[i] > band) continue;
    const t = edgeTangent(grid, edge, i);
    if (!Number.isNaN(t) && axisDiff(ang[i], t) <= STRIPE_AGREE_RAD) tang[i] = 1;
  }

  // Dominant pass axis: weighted mean of doubled angles (undirected).
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) {
    if (!scored[i] || tang[i]) continue;
    sx += wt[i] * Math.cos(2 * ang[i]);
    sy += wt[i] * Math.sin(2 * ang[i]);
  }
  const theta = Math.atan2(sy, sx) / 2;
  const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? y * w + x : -1);

  // A: row agreement.
  let aSum = 0, wSum = 0;
  for (let i = 0; i < n; i++) {
    if (!scored[i]) continue;
    wSum += wt[i];
    if (tang[i]) { aSum += wt[i]; continue; }
    const onAxis = axisDiff(ang[i], theta) <= Math.PI / 8;
    const [dx, dy] = stepFor(onAxis ? theta : ang[i]);
    const x = i % w, y = (i / w) | 0;
    let seen = 0, match = 0;
    for (const s of [1, -1]) {
      const j = at(x + s * dx, y + s * dy);
      if (j < 0 || !scored[j]) continue;
      seen++;
      if (tang[j] || axisDiff(ang[i], ang[j]) <= STRIPE_AGREE_RAD) match++;
    }
    if (seen) aSum += (wt[i] * match) / seen;
  }
  const A = wSum > 0 ? aSum / wSum : 0;

  // S: row spacing, scanning across the dominant axis.
  const bands = bandRuns(grid, scored, tang, ang, wt, theta);
  let S = 1;
  if (bands.length) {
    const widths = bands.map((b) => b.width).sort((a, b) => a - b);
    const median = widths[widths.length >> 1];
    const useMedian = median >= deck * 0.5 && median <= deck;
    // Diagonal passes sit on the diagonal lines of cells: a pass is floor(deck·√2) lines wide.
    const [dx, dy] = stepFor(theta);
    const diagUnit = dx !== 0 && dy !== 0 ? Math.max(1, Math.floor(deck * Math.SQRT2 + 1e-6)) * Math.SQRT1_2 : 0;
    let sSum = 0, sW = 0;
    for (const b of bands) {
      const fit = Math.max(fitTo(b.width, deck), useMedian ? fitTo(b.width, median) : 0, diagUnit ? fitTo(b.width, diagUnit) : 0);
      sSum += b.weight * fit;
      sW += b.weight;
    }
    S = sW > 0 ? sSum / sW : 1;
  }

  // F: short fragments (4-connected patches of same-direction heading).
  const minCells = Math.max(6, Math.round(3 * deck * deck));
  const comp = new Int32Array(n).fill(-1);
  const stack: number[] = [];
  const members: number[] = [];
  let fragW = 0;
  for (let s = 0; s < n; s++) {
    if (!scored[s] || tang[s] || comp[s] >= 0) continue;
    comp[s] = s;
    stack.push(s);
    members.length = 0;
    while (stack.length) {
      const i = stack.pop()!;
      members.push(i);
      const x = i % w, y = (i / w) | 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const j = at(x + dx, y + dy);
        if (j < 0 || !scored[j] || tang[j] || comp[j] >= 0) continue;
        if (dirDiff(ang[i], ang[j]) > STRIPE_AGREE_RAD) continue;
        comp[j] = s;
        stack.push(j);
      }
    }
    if (members.length >= minCells) continue;
    // A thin run at least RUN_CELLS long along its own heading is a short stripe, not a fragment.
    const fx = Math.cos(ang[s]), fy = Math.sin(ang[s]);
    let lo = Infinity, hi = -Infinity;
    for (const i of members) {
      const t = (i % w) * fx + ((i / w) | 0) * fy;
      if (t < lo) lo = t;
      if (t > hi) hi = t;
    }
    if (hi - lo + 1 >= Math.max(RUN_CELLS, 2 * deck)) continue;
    for (const i of members) fragW += wt[i];
  }
  const F = wSum > 0 ? fragW / wSum : 0;

  const raw = (0.65 * A + 0.35 * S) * (1 - 0.6 * F);
  return Math.round(100 * Math.max(0, Math.min(1, raw)));
}

// ─── Inputs from the layout ─────────────────────────────────────────────

/**
 * Round islands a circling pass may go around: props with a radius, and
 * compact islands of bed/obstacle cells inside the lawn (not touching the
 * grid edge, ≤ 400 cells, bounding box aspect 0.6–1.67).
 */
export function stripeIslands(layout: Layout, grid: Grid): StripeIsland[] {
  const out: StripeIsland[] = [];
  for (const p of layout.props) {
    if (p.solid && p.r && p.r > 0) out.push({ x: p.x, y: p.y, r: p.r });
  }
  const { w, h, cellM } = grid;
  const seen = new Uint8Array(w * h);
  const stack: number[] = [];
  for (let s = 0; s < w * h; s++) {
    const t = grid.type[s];
    if (seen[s] || (t !== T_BED && t !== T_OBSTACLE)) continue;
    seen[s] = 1;
    stack.push(s);
    let size = 0, x0 = w, x1 = -1, y0 = h, y1 = -1, border = false, sumX = 0, sumY = 0;
    while (stack.length) {
      const i = stack.pop()!;
      const x = i % w, y = (i / w) | 0;
      size++; sumX += x; sumY += y;
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) border = true;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const xx = x + dx, yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
        const j = yy * w + xx;
        const tj = grid.type[j];
        if (seen[j] || (tj !== T_BED && tj !== T_OBSTACLE)) continue;
        seen[j] = 1;
        stack.push(j);
      }
    }
    const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
    const aspect = bw / bh;
    if (border || size > 400 || aspect < 0.6 || aspect > 1 / 0.6) continue;
    out.push({ x: (sumX / size + 0.5) * cellM, y: (sumY / size + 0.5) * cellM, r: (Math.max(bw, bh) / 2) * cellM });
  }
  return out;
}

/**
 * Front of house: the `nature-strip` zone, plus the half of the lot (grass
 * bounding box) on the side nearest the spawn, the street side.
 */
export function frontOfHouse(layout: Layout, grid: Grid): Uint8Array {
  const { w, h, cellM } = grid;
  const front = new Uint8Array(w * h);
  let x0 = w, x1 = -1, y0 = h, y1 = -1;
  for (let i = 0; i < w * h; i++) {
    if (grid.zone[i] === Z_NATURE_STRIP) front[i] = 1;
    if (grid.type[i] !== T_GRASS) continue;
    const x = i % w, y = (i / w) | 0;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y);
  }
  if (x1 < 0) return front;
  const sx = layout.spawn.x / cellM, sy = layout.spawn.y / cellM;
  const sides = [
    { d: sx - x0, side: 'left' }, { d: x1 + 1 - sx, side: 'right' },
    { d: sy - y0, side: 'top' }, { d: y1 + 1 - sy, side: 'bottom' },
  ].sort((a, b) => a.d - b.d);
  const side = sides[0].side;
  const mx = (x0 + x1 + 1) / 2, my = (y0 + y1 + 1) / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const cx = x + 0.5, cy = y + 0.5;
      const inFront = side === 'left' ? cx < mx : side === 'right' ? cx >= mx : side === 'top' ? cy < my : cy >= my;
      if (inFront) front[y * w + x] = 1;
    }
  }
  return front;
}

// ─── Tiers and the bonus ────────────────────────────────────────────────

export type StripeTier = 'bowling-green' | 'tidy' | 'bit-wavy' | 'dog';

export const STRIPE_TIER_COPY: Record<StripeTier, string> = {
  'bowling-green': 'Bowling green',
  tidy: 'Tidy',
  'bit-wavy': 'Bit wavy',
  dog: 'Did a dog do this?',
};

/** ≥ 90 bowling green, ≥ 75 tidy, ≥ 55 bit wavy, below that a dog did it. */
export function stripeTier(score: number): StripeTier {
  if (score >= 90) return 'bowling-green';
  if (score >= 75) return 'tidy';
  if (score >= 55) return 'bit-wavy';
  return 'dog';
}

/** Tip points on top of `tipPct`: +5 at ≥ 90, +3 at ≥ 75, +1 at ≥ 55, else 0. Never negative. */
export function stripeBonusPct(score: number): number {
  if (score >= 90) return 5;
  if (score >= 75) return 3;
  if (score >= 55) return 1;
  return 0;
}

/**
 * Pay the stripe bonus on a result that carries a `stripeScore`: +pct of the
 * fee, rounded as the tip is, added after the tip, plus the pattern's point or
 * two (patternBonusPct) when the result names one. Abandoned jobs get 0.
 * Idempotent (a result that already has `stripeBonusPct` comes back as is),
 * and a result without a score is returned unchanged.
 */
export function withStripeBonus(r: JobResult): JobResult {
  if (r.stripeBonusPct !== undefined || r.stripeScore === undefined) return r;
  const pct = r.abandoned ? 0 : stripeBonusPct(r.stripeScore) + patternBonusPct(r.stripePattern, r.stripeScore);
  const aud = Math.round((r.feeAud * pct) / 100);
  return {
    ...r,
    stripeBonusPct: pct,
    stripeBonusAud: aud,
    // Feature P9: the honest-bloke and extra-mile money stays in (0 when absent).
    totalAud: Math.max(0, r.feeAud + r.tipAud + aud + bonusAud(r) - r.penaltiesAud),
  };
}

// ─── Patterns ───────────────────────────────────────────────────────────
//
// What the finished lawn reads as (docs/mowing-patterns.md). Read off the
// grid, never off what anyone meant to do, so a player who mows a
// checkerboard by hand gets it named too. Needs `grid.stripe2` for the
// crossed ones (SimOptions.crossStripes).
//
//   crossed  ≥ 40% of the scored cells carry a crossing pass underneath:
//            the top set diagonal → diamond; square to the lot → checkerboard,
//            or the sports hatch when its bands are wide (≥ 1.25 m × gameScale,
//            the footy ground's two-pitch-wide squares)
//   rings    no one axis dominates (doubled-angle coherence < 0.6) and ≥ 60%
//            of the cells run along the contours of the distance to the lawn's
//            edge (within 25°): laps in from the edge
//   (read off the inside of the lawn, past the headland; a lawn too small to
//   have an inside is read whole, can't be rings, and without an axis at all
//   (coherence < 0.35) is plain stripes)
//   diagonal a dominant axis within 15° of 45°
//   double   a dominant axis whose bands are ≥ 1.6 deck widths (passes in pairs)
//   stripes  anything else that was mowed enough to rate

export type StripePattern = NonNullable<JobResult['stripePattern']>;

/** In the order the pickers show them. */
export const STRIPE_PATTERNS: readonly StripePattern[] = ['stripes', 'double', 'diagonal', 'checker', 'diamond', 'hatch', 'rings'];

/** Names and one-liners (Settings, the pause sheet, the HUD chip, the results line). */
export const PATTERN_COPY: Record<StripePattern, { name: string; sub: string }> = {
  stripes: { name: 'Stripes', sub: 'Up and back, one deck wide. The classic.' },
  double: { name: 'Double-wide', sub: 'Two passes each way: bands twice the deck.' },
  diagonal: { name: 'Diagonal', sub: 'Stripes at 45° across the lot.' },
  checker: { name: 'Checkerboard', sub: 'Stripes, then a second set straight across them.' },
  diamond: { name: 'Diamond', sub: 'Two diagonal sets, crossed: squares on their points.' },
  hatch: { name: 'Sports hatch', sub: 'The footy-ground crosshatch: big squares, lined up with the pitch.' },
  rings: { name: 'Rings', sub: 'Laps in from the edge, each one the other way round.' },
};

/**
 * Tip points a tidy pattern adds on top of the stripe bonus (score ≥ 75):
 * +1 for the one-set patterns, +2 for the crossed ones, which take a second
 * full set of passes. Plain stripes add nothing.
 */
export const PATTERN_BONUS_PCT: Record<StripePattern, number> = {
  stripes: 0, double: 1, diagonal: 1, rings: 1, checker: 2, diamond: 2, hatch: 2,
};

export function patternBonusPct(p: StripePattern | undefined, score: number): number {
  return p && score >= 75 ? PATTERN_BONUS_PCT[p] : 0;
}

/**
 * The pattern's name for the results line ("Stripes · Checkerboard"), when the
 * lawn reads as one other than plain stripes and tidily enough to see (a
 * stripe score of 55 or more); '' otherwise.
 */
export function patternName(r: Pick<JobResult, 'stripePattern' | 'stripeScore'>): string {
  const p = r.stripePattern;
  return p && p !== 'stripes' && (r.stripeScore ?? 0) >= 55 ? PATTERN_COPY[p].name : '';
}

export interface PatternOptions {
  /** The mower's deck width in cells (MowerState.deckCells). */
  deckCells: number;
  /** Layout.gameScale (1 when absent): the sports hatch's squares scale with it. */
  gameScale?: number;
}

const DIAG_TOL = (15 * Math.PI) / 180;
/** A crossed pattern: this share of the scored cells carry a crossing pass underneath (the headland lap never does). */
const CROSSED_SHARE = 0.4;

/**
 * Share of the scored cells whose heading runs along the contour of the
 * distance to the lawn's edge there (a 3-4 chamfer distance from anything not
 * grass; its gradient's normal), within STRIPE_AGREE_RAD. Cells on a ridge,
 * where the distance has no slope, aren't counted.
 */
function lapShare(grid: Grid, scored: Uint8Array, ang: Float32Array, d: Float32Array): number {
  const n = d.length;
  let seen = 0, along = 0;
  for (let i = 0; i < n; i++) {
    if (!scored[i]) continue;
    const t = edgeTangent(grid, d, i);
    if (Number.isNaN(t)) continue;
    seen++;
    if (axisDiff(ang[i], t) <= STRIPE_AGREE_RAD) along++;
  }
  return seen ? along / seen : 0;
}

/**
 * Distance from each cell to anything not grass (the lawn's edge), a 3-4
 * chamfer: 3 per cell straight, 4 diagonal. 0 off the lawn.
 */
export function edgeChamfer(grid: Grid): Float32Array {
  const { w, h } = grid;
  const n = w * h;
  const d = new Float32Array(n);
  for (let i = 0; i < n; i++) d[i] = grid.type[i] === T_GRASS ? 1e9 : 0;
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : d[y * w + x]);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (!d[i]) continue;
    d[i] = Math.min(d[i], at(x - 1, y) + 3, at(x, y - 1) + 3, at(x - 1, y - 1) + 4, at(x + 1, y - 1) + 4);
  }
  for (let y = h - 1; y >= 0; y--) for (let x = w - 1; x >= 0; x--) {
    const i = y * w + x;
    if (!d[i]) continue;
    d[i] = Math.min(d[i], at(x + 1, y) + 3, at(x, y + 1) + 3, at(x + 1, y + 1) + 4, at(x - 1, y + 1) + 4);
  }
  return d;
}

/** The lawn edge's direction at cell i (the chamfer's contour there), or NaN on a ridge where it has no slope. */
function edgeTangent(grid: Grid, d: Float32Array, i: number): number {
  const { w, h } = grid;
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : d[y * w + x]);
  const x = i % w, y = (i / w) | 0;
  const gx = at(x + 1, y) - at(x - 1, y), gy = at(x, y + 1) - at(x, y - 1);
  if (Math.abs(gx) + Math.abs(gy) < 2) return NaN;
  return Math.atan2(gy, gx) + Math.PI / 2;
}
const isDiagonal = (t: number) => Math.min(axisDiff(t, Math.PI / 4), axisDiff(t, -Math.PI / 4)) <= DIAG_TOL;

/** The pattern the cut grid reads as, or null when too little was mowed to rate (as stripeScore). Pure. */
export function stripePattern(grid: Grid, opts: PatternOptions): StripePattern | null {
  const { w, h, cellM } = grid;
  const n = w * h;
  const deck = Math.max(1, opts.deckCells || 1);
  const gs = opts.gameScale ?? 1;
  const s2 = grid.stripe2;
  const scored = new Uint8Array(n);
  const ang = new Float32Array(n);
  const wt = new Float32Array(n);
  // Read the pattern off the inside of the lawn: the headland lap goes round whatever the pattern is.
  const edge = edgeChamfer(grid);
  const deep = (deck + 1.5) * 3;
  let all = 0;
  for (let i = 0; i < n; i++) if (grid.type[i] === T_GRASS && grid.cut[i] && !grid.trim[i]) all++;
  let inside = 0;
  for (let i = 0; i < n; i++) if (grid.type[i] === T_GRASS && grid.cut[i] && !grid.trim[i] && edge[i] > deep) inside++;
  const useAll = inside < Math.max(STRIPE_MIN_CELLS, all * 0.25);
  let count = 0, crossed = 0, sx = 0, sy = 0, bx = 0, by = 0;
  for (let i = 0; i < n; i++) {
    if (grid.type[i] !== T_GRASS || !grid.cut[i] || grid.trim[i]) continue;
    if (!useAll && edge[i] <= deep) continue;
    scored[i] = 1;
    const a = grid.stripe[i] * Q;
    ang[i] = a;
    wt[i] = 1;
    count++;
    sx += Math.cos(2 * a);
    sy += Math.sin(2 * a);
    if (s2 && s2[i] && axisGap(grid.stripe[i], s2[i]) >= CROSS_Q) {
      crossed++;
      bx += Math.cos(2 * a);
      by += Math.sin(2 * a);
    }
  }
  if (all < STRIPE_MIN_CELLS) return null;
  const none = new Uint8Array(n);
  const medianBand = (theta: number): number => {
    const widths = bandRuns(grid, scored, none, ang, wt, theta).map((b) => b.width).sort((a, b) => a - b);
    return widths.length ? widths[widths.length >> 1] : 0;
  };

  if (crossed >= CROSSED_SHARE * count) {
    const top = Math.atan2(by, bx) / 2;
    if (isDiagonal(top)) return 'diamond';
    return medianBand(top) * cellM >= 1.25 * gs ? 'hatch' : 'checker';
  }
  const R = Math.hypot(sx, sy) / count;
  // Laps: no one axis dominates, and the passes run round the lawn (along the contours of the distance to its edge).
  // (A lawn too small to have an inside is all headland: it can't tell laps from stripes.)
  if (!useAll && R < 0.6 && lapShare(grid, scored, ang, edge) >= 0.6) return 'rings';
  // No axis to speak of: plain (if messy) stripes, not a pattern.
  if (R < 0.35) return 'stripes';
  const theta = Math.atan2(sy, sx) / 2;
  if (isDiagonal(theta)) return 'diagonal';
  return medianBand(theta) >= 1.6 * deck ? 'double' : 'stripes';
}
